import React from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
} from "recharts";

interface AnalyticsData {
  salesData: Array<{
    month: string;
    sales: number;
  }>;
  categoryData: Array<{
    category: string;
    sales: number;
  }>;
  customerData: Array<{
    customer: string;
    orders: number;
    total: number;
  }>;
  statusData: Array<{
    status: string;
    count: number;
  }>;
  // Yeni eklenen veriler
  regionalData: Array<{
    region: string;
    sales: number;
    orders: number;
    customers: number;
  }>;
  personnelData: Array<{
    name: string;
    role: string;
    performance: number;
    orders: number;
  }>;
  warehouseData: Array<{
    warehouse: string;
    capacity: number;
    usage: number;
    products: number;
  }>;
  timeSeriesData: Array<{
    date: string;
    sales: number;
    orders: number;
    customers: number;
  }>;
}

interface AnalyticsDashboardProps {
  data: AnalyticsData;
}

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
  "#FFC658",
  "#8DD1E1",
];

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ data }) => {
  return (
    <div className="analytics-dashboard">
      <h2>Kapsamlı İş Raporları</h2>

      <div className="charts-grid">
        {/* 1. Türkiye Haritası İstatistikleri */}
        <div className="chart-container full-width">
          <h3>Bölgesel Satış Dağılımı (Türkiye)</h3>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={data.regionalData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="region" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="sales"
                fill="#8884d8"
                name="Satış (₺)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="orders"
                stroke="#82ca9d"
                name="Sipariş Sayısı"
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="customers"
                fill="#ffc658"
                fillOpacity={0.3}
                name="Müşteri Sayısı"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* 2. Çizgi Grafiği - Zaman Serisi */}
        <div className="chart-container">
          <h3>Zaman Serisi Analizi</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="sales"
                stroke="#8884d8"
                strokeWidth={2}
                name="Satış (₺)"
                dot={{ fill: "#8884d8", strokeWidth: 2, r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="orders"
                stroke="#82ca9d"
                strokeWidth={2}
                name="Sipariş Sayısı"
                dot={{ fill: "#82ca9d", strokeWidth: 2, r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="customers"
                stroke="#ffc658"
                strokeWidth={2}
                name="Müşteri Sayısı"
                dot={{ fill: "#ffc658", strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 3. Baklava Dilimi - Kategori Dağılımı */}
        <div className="chart-container">
          <h3>Kategori Dağılımı (Baklava Dilimi)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent, value }) =>
                  `${name}: ${value}₺ (${(percent * 100).toFixed(1)}%)`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="sales"
              >
                {data.categoryData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value}₺`, "Satış"]} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* 4. Kişiler İstatistiği */}
        <div className="chart-container">
          <h3>Personel Performans Analizi</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.personnelData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="performance" fill="#0088FE" name="Performans (%)" />
              <Bar dataKey="orders" fill="#00C49F" name="Sipariş Sayısı" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 5. Depo Kullanım Analizi */}
        <div className="chart-container">
          <h3>Depo Kullanım Oranları</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.warehouseData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="warehouse" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="usage" fill="#FFBB28" name="Kullanım (%)" />
              <Bar dataKey="products" fill="#FF8042" name="Ürün Sayısı" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 6. Sipariş Durumu Dağılımı */}
        <div className="chart-container">
          <h3>Sipariş Durumu Dağılımı</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent, value }) =>
                  `${name}: ${value} (${(percent * 100).toFixed(1)}%)`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {data.statusData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* 7. En İyi Müşteriler */}
        <div className="chart-container">
          <h3>En İyi Müşteriler</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.customerData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="customer" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value}₺`, "Toplam Satış"]} />
              <Legend />
              <Bar dataKey="total" fill="#8884d8" name="Toplam Satış (₺)" />
              <Bar dataKey="orders" fill="#82ca9d" name="Sipariş Sayısı" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 8. Aylık Satış Trendi */}
        <div className="chart-container">
          <h3>Aylık Satış Trendi</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.salesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value}₺`, "Satış"]} />
              <Legend />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#8884d8"
                fill="#8884d8"
                fillOpacity={0.3}
                name="Satış (₺)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
